/**
 * CustomPermissions
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, ListGroup } from 'react-bootstrap';
import TwoStepControl from 'components/interaction/TwoStepControl/index.jsx';
import SelectUsers from 'components/interaction/SelectUsers/index.jsx';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import { idValues } from 'utils/arrays.js';
import { getIdNumber } from 'utils/userIdRole.js';
import PermissionSelect from './PermissionSelect.jsx';
import messages from './messages.js';
import getSelected from './getSelected.js';
import PermissionsModal from './PermissionsModal.jsx';
import useUserRoles from '../useUserRoles.js';

const STAGE_NONE = Symbol('No modal');
const STAGE_USERS = Symbol('Select users');
const STAGE_PERMISSIONS = Symbol('Select permissions');

function CustomPermissions(props) {
  const { onChange, settings, template, className } = props;
  const { enabled, customPermissions } = settings;
  const permissionsList = idValues(customPermissions);
  const userRoles = useUserRoles();

  const [stage, setStage] = useState(STAGE_NONE);
  const [users, setUsers] = useState([]);

  function handleChange(newPermissions) {
    const updated = idValues(newPermissions).map((o) => ({
      user: { id: o.id, roleType: o.roleType },
      permissions: o.permissions,
    }));
    onChange('customPermissions', updated);
  }

  const handleUser = (role) => (permissions) => {
    if (!onChange) {
      return;
    }
    const id = getIdNumber(role);
    customPermissions[id].permissions = permissions;
    handleChange(customPermissions);
  };

  function handleDelete(role) {
    const id = getIdNumber(role);
    delete customPermissions[id];
    customPermissions.list = customPermissions.list.filter((i) => i !== id);
    handleChange(customPermissions);
  }

  function handleSelect(roles) {
    setUsers(roles);
    setStage(STAGE_PERMISSIONS);
  }

  function handleAdd(permissions) {
    const result = { ...customPermissions };
    users.forEach((role) => {
      const id = getIdNumber(role);
      if (customPermissions[id]) {
        return;
      }
      result[id] = { ...role, permissions };
      result.list.push(id);
    });
    setStage(STAGE_NONE);
    handleChange(result);
  }

  function handleClose(val) {
    setStage(val ? STAGE_PERMISSIONS : STAGE_NONE);
  }

  const fetchOptions = {
    url: 'sa/users',
    version: 2,
    typeField: 'type',
  };

  return (
    <section className={className}>
      {permissionsList.length > 0 && (
        <>
          <h5 className="text-gray"><FormattedMessage {...messages.exceptions} /></h5>
          <ListGroup className="mb-3">
            {permissionsList.map((user) => (
              <ListGroup.Item key={user.id} className="px-0 text-gray">
                <UserAvatar user={user} />
                <TwoStepControl
                  Control={PermissionSelect}
                  options={template.options}
                  onChange={handleUser(user)}
                  onDelete={() => handleDelete(user)}
                  confirmDeleteText={{ ...messages.confirmDeleteException, values: user }}
                  value={getSelected(user.permissions, template.options)}
                  disabled={!enabled}
                />
              </ListGroup.Item>
            ))}
          </ListGroup>
        </>
      )}
      <Button variant="light" onClick={() => setStage(STAGE_USERS)} disabled={!enabled}><FormattedMessage {...messages.addException} /></Button>
      <SelectUsers
        show={stage === STAGE_USERS}
        fetchOptions={fetchOptions}
        value={permissionsList}
        exclusions={customPermissions.list}
        onChange={handleSelect}
        onHide={handleClose}
        roles={userRoles}
      />
      {stage === STAGE_PERMISSIONS && (
        <PermissionsModal
          options={template.options}
          onChange={handleAdd}
          onHide={() => setStage(STAGE_NONE)}
        />
      )}
    </section>
  );
}

CustomPermissions.propTypes = {
  onChange: PropTypes.func,
  settings: PropTypes.object,
  template: PropTypes.object,
  className: PropTypes.string,
};

export default CustomPermissions;
